import { zodResolver } from '@hookform/resolvers/zod';
import { ROLE_LABELS, STAGE_KIND_LABELS, USER_ROLES, changePasswordSchema, formatDateTime, userCreateSchema, type UserRole } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Columns3, KeyRound, Plug, Plus, Users, Workflow } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import { NavLink, Navigate, Route, Routes } from 'react-router';
import { toast } from 'sonner';
import type { z } from 'zod';
import { PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Field, Input, Select } from '@/components/ui/form';
import { Badge, Card, CardHeader, Dialog, DialogContent, PageLoader } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { keys, useStages } from '@/lib/queries';
import type { Stage } from '@/lib/types';
import { cn } from '@/lib/utils';
import { IntegrationsSettings } from '@/features/integrations/IntegrationsSettings';
import { AutomationsSettings } from '@/features/automations/AutomationsSettings';

export function SettingsPage() {
  const { can } = useAuth();
  const tabs = [
    { to: '/settings/account', label: 'החשבון שלי', icon: <KeyRound />, show: true },
    { to: '/settings/users', label: 'משתמשים', icon: <Users />, show: can('users.manage') },
    { to: '/settings/pipeline', label: 'שלבי פייפליין', icon: <Columns3 />, show: can('pipeline.configure') },
    { to: '/settings/automations', label: 'אוטומציות', icon: <Workflow />, show: can('crm.read') },
    { to: '/settings/integrations', label: 'חיבורים', icon: <Plug />, show: can('integrations.manage') },
  ].filter((t) => t.show);
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="הגדרות" />
      <div className="grid gap-5 md:grid-cols-[200px_1fr]">
        <nav className="flex gap-1 md:flex-col">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                cn('flex items-center gap-2 rounded-control px-3 py-2 text-sm font-semibold [&_svg]:size-4', isActive ? 'bg-matcha-100 text-matcha-900' : 'text-ink-soft hover:bg-sunken')
              }
            >
              {t.icon}
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="min-w-0">
          <Routes>
            <Route index element={<Navigate to="/settings/account" replace />} />
            <Route path="*" element={<Navigate to="/settings/account" replace />} />
            <Route path="account" element={<AccountSettings />} />
            {can('users.manage') && <Route path="users" element={<UsersSettings />} />}
            {can('pipeline.configure') && <Route path="pipeline" element={<PipelineSettings />} />}
            <Route path="automations" element={<AutomationsSettings />} />
            {can('integrations.manage') && <Route path="integrations" element={<IntegrationsSettings />} />}
          </Routes>
        </div>
      </div>
    </div>
  );
}

function AccountSettings() {
  const { me } = useAuth();
  type Out = z.output<typeof changePasswordSchema>;
  const { register, handleSubmit, reset, formState } = useForm<Out>({ resolver: zodResolver(changePasswordSchema) });
  const change = useMutation({
    mutationFn: (v: Out) => api.post('/auth/change-password', v),
    onSuccess: () => {
      toast.success('הסיסמה עודכנה. שאר החיבורים שלך נותקו.');
      reset({ currentPassword: '', newPassword: '' });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Card>
      <CardHeader title="החשבון שלי" icon={<KeyRound />} />
      <div className="p-4">
        <p className="text-sm">
          {me?.user.fullName} · <span className="ltr">{me?.user.email}</span> · {me && ROLE_LABELS[me.user.role]}
        </p>
        <form onSubmit={handleSubmit((v) => change.mutate(v))} className="mt-4 grid max-w-md gap-4" noValidate>
          <Field label="סיסמה נוכחית" error={formState.errors.currentPassword?.message}>
            {(id) => <Input id={id} type="password" autoComplete="current-password" {...register('currentPassword')} />}
          </Field>
          <Field label="סיסמה חדשה" error={formState.errors.newPassword?.message} hint="לפחות 10 תווים">
            {(id) => <Input id={id} type="password" autoComplete="new-password" {...register('newPassword')} />}
          </Field>
          <div>
            <Button type="submit" loading={change.isPending}>עדכון סיסמה</Button>
          </div>
        </form>
      </div>
    </Card>
  );
}

interface UserRow {
  id: string;
  email: string;
  fullName: string;
  phone: string | null;
  role: UserRole;
  isActive: boolean;
  lastLoginAt: string | null;
}

function UsersSettings() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState<UserRow | null>(null);
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<UserRow[]>('/users') });
  const update = useMutation({
    mutationFn: (v: { id: string; body: Partial<UserRow> }) => api.patch(`/users/${v.id}`, v.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: keys.users });
      toast.success('המשתמש עודכן');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!users.data) return <PageLoader />;
  return (
    <Card>
      <CardHeader title="משתמשים" icon={<Users />} actions={<Button size="sm" onClick={() => setCreating(true)}><Plus /> משתמש חדש</Button>} />
      <ul className="divide-y divide-line/70">
        {users.data.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="font-semibold">
                {u.fullName} {!u.isActive && <Badge variant="overdue">מושבת</Badge>}
              </div>
              <div className="text-xs text-muted">
                <span className="ltr">{u.email}</span> · {u.lastLoginAt ? `כניסה אחרונה ${formatDateTime(u.lastLoginAt)}` : 'טרם התחבר'}
              </div>
            </div>
            <Select
              value={u.role}
              disabled={u.id === me?.user.id}
              onChange={(e) => update.mutate({ id: u.id, body: { role: e.target.value as UserRole } })}
              className="w-36"
              aria-label="תפקיד"
            >
              {USER_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </Select>
            {u.id !== me?.user.id && (
              <Button size="sm" variant="ghost" onClick={() => setResetting(u)}>איפוס סיסמה</Button>
            )}
            {u.id !== me?.user.id && (
              <Button size="sm" variant={u.isActive ? 'ghost' : 'secondary'} onClick={() => update.mutate({ id: u.id, body: { isActive: !u.isActive } })}>
                {u.isActive ? 'השבתה' : 'הפעלה'}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <NewUserDialog open={creating} onOpenChange={setCreating} />
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </Card>
  );
}

/** An admin sets a new password for another user (also unlocks the account and signs out their sessions). */
function ResetPasswordDialog({ user, onClose }: { user: UserRow; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.patch(`/users/${user.id}`, { password }),
    onSuccess: () => {
      toast.success(`הסיסמה של ${user.fullName} עודכנה`);
      onClose();
    },
    onError: (e) => setError(errorMessage(e)),
  });
  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    if (password.length < 10) return setError('לפחות 10 תווים');
    setError(null);
    save.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="איפוס סיסמה" description={`סיסמה חדשה עבור ${user.email}. המשתמש ינותק מכל החיבורים הפתוחים.`}>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field label="סיסמה חדשה" error={error ?? undefined} hint="לפחות 10 תווים">
            {(id) => <Input id={id} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={onClose}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function NewUserDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  type In = z.input<typeof userCreateSchema>;
  type Out = z.output<typeof userCreateSchema>;
  const { register, handleSubmit, reset, formState } = useForm<In, unknown, Out>({
    resolver: zodResolver(userCreateSchema),
    defaultValues: { role: 'sales', fullName: '', email: '', password: '' },
  });
  const create = useMutation({
    mutationFn: (v: Out) => api.post('/users', v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: keys.users });
      toast.success('המשתמש נוצר');
      reset();
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const e = formState.errors;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="משתמש חדש" description="מסרו למשתמש את הסיסמה הראשונית בערוץ מאובטח; הוא יוכל להחליף אותה בהגדרות.">
        <form onSubmit={handleSubmit((v) => create.mutate(v))} className="grid gap-4" noValidate>
          <Field label="שם מלא" error={e.fullName?.message}>{(id) => <Input id={id} {...register('fullName')} />}</Field>
          <Field label="אימייל" error={e.email?.message}>{(id) => <Input id={id} type="email" dir="ltr" className="text-start" {...register('email')} />}</Field>
          <Field label="תפקיד">
            {(id) => (
              <Select id={id} {...register('role')}>
                {USER_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </Select>
            )}
          </Field>
          <Field label="סיסמה ראשונית" error={e.password?.message} hint="לפחות 10 תווים">
            {(id) => <Input id={id} type="password" autoComplete="new-password" {...register('password')} />}
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={create.isPending}>יצירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PipelineSettings() {
  const qc = useQueryClient();
  const stages = useStages();
  const [name, setName] = useState('');
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: keys.stages });
    void qc.invalidateQueries({ queryKey: keys.board });
  };
  const create = useMutation({
    mutationFn: () => api.post('/pipeline/stages', { name: name.trim(), color: '#6b8f3a', kind: 'open' }),
    onSuccess: () => {
      setName('');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const update = useMutation({
    mutationFn: (v: { id: string; body: Partial<Stage> }) => api.patch(`/pipeline/stages/${v.id}`, v.body),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.put('/pipeline/stages/order', { stageIds: ids }),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (!stages.data) return <PageLoader />;
  const list = stages.data;
  const moveBy = (i: number, d: number) => {
    const ids = list.map((s) => s.id);
    const [x] = ids.splice(i, 1);
    ids.splice(i + d, 0, x!);
    reorder.mutate(ids);
  };
  return (
    <Card>
      <CardHeader title="שלבי פייפליין" icon={<Columns3 />} />
      <ul className="divide-y divide-line/70">
        {list.map((s, i) => (
          <li key={s.id} className={cn('flex items-center gap-3 px-4 py-2.5', !s.isActive && 'opacity-50')}>
            <input
              type="color"
              value={s.color}
              aria-label={`צבע ${s.name}`}
              onChange={(e) => update.mutate({ id: s.id, body: { color: e.target.value } })}
              className="size-7 cursor-pointer rounded border border-line bg-transparent"
            />
            <input
              defaultValue={s.name}
              aria-label="שם השלב"
              onBlur={(e) => e.target.value.trim() && e.target.value !== s.name && update.mutate({ id: s.id, body: { name: e.target.value.trim() } })}
              className="flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 font-semibold hover:border-line focus:border-matcha-500 focus:outline-none"
            />
            <Badge variant={s.kind === 'won' ? 'gold' : s.kind === 'lost' ? 'overdue' : 'neutral'}>{STAGE_KIND_LABELS[s.kind]}</Badge>
            <div className="flex">
              <Button size="icon-sm" variant="ghost" disabled={i === 0} onClick={() => moveBy(i, -1)} aria-label="למעלה"><ArrowUp /></Button>
              <Button size="icon-sm" variant="ghost" disabled={i === list.length - 1} onClick={() => moveBy(i, 1)} aria-label="למטה"><ArrowDown /></Button>
            </div>
            {s.kind !== 'won' && (
              <Button size="sm" variant="ghost" onClick={() => update.mutate({ id: s.id, body: { isActive: !s.isActive } })}>
                {s.isActive ? 'השבתה' : 'הפעלה'}
              </Button>
            )}
          </li>
        ))}
      </ul>
      <form
        className="flex gap-2 border-t border-line p-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="שם שלב חדש" className="max-w-xs" />
        <Button type="submit" variant="secondary" loading={create.isPending}><Plus /> הוספת שלב</Button>
      </form>
    </Card>
  );
}
